import React, { FC } from 'react';
import { Col, Row } from 'antd';
import { dateFormatter } from 'common/helpers/date.helper';
import { InfoTooltip } from 'common/components/InfoTooltip';
import { EMetricType, EMetricTypeLabel } from 'common/models/commonModel';
import { ITrackStoreWeeklyGrid } from 'entities/TrackStore/TrackStore.models';
import { getWeekDates, mapDailyMetrics } from 'entities/TrackStore/TrackStore.helper';

interface IComponentProps {
  weeklyGrid: ITrackStoreWeeklyGrid | null;
}

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 120px) repeat(7, 1fr)',
    gap: 4,
  },
  row: {
    display: 'contents',
  },
  col: {
    height: 24,
    borderRadius: 4,
  },
};

export const TrackStoreWeeklyGrid: FC<IComponentProps> = ({ weeklyGrid }) => {
  const dates = getWeekDates(weeklyGrid?.weekStart);
  const dailyMetrics = mapDailyMetrics(weeklyGrid?.days, weeklyGrid?.weekStart);

  const renderRow = (type: EMetricType) => {
    return (
      <div style={styles.row}>
        <div className="mr-4 flex flex-align-center">
          <span className="ff-regular fs-xxs ellipsis">{EMetricTypeLabel[type]}</span>
        </div>
        {dailyMetrics[type].map((item, index) => {
          return <div key={index} style={{ ...styles.col, background: item >= 100 ? '#5dca8b' : '#ff4e4e' }} />;
        })}
      </div>
    );
  };

  return (
    <div>
      <div className="mb-5 flex flex-align-center flex-justify-between">
        <span className="ff-medium fs-xxs">Weekly Data</span>
        <InfoTooltip
          title={
            <div>
              <div className="mb-4 ff-medium fs-xxxs color-gray-1">Spot which days hit target and which missed!</div>
              <Row className="mb-4">
                <Col span={6} className="ff-regular fs-xxxs color-green">
                  Green
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={16} className="ff-regular fs-xxxs">
                  target hit
                </Col>
              </Row>
              <Row className="mb-4">
                <Col span={6} className="ff-regular fs-xxxs color-red">
                  Red
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={16} className="ff-regular fs-xxxs">
                  target missed
                </Col>
              </Row>
              <div className="mb-4 ff-medium fs-xxxs color-gray-1">Shows daily status for:</div>
              <Row className="mb-4">
                <Col span={6} className="ff-regular fs-xxxs">
                  Sales
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={16} className="ff-regular fs-xxxs">
                  Total Sales
                </Col>
              </Row>
              <Row className="mb-4">
                <Col span={6} className="ff-regular fs-xxxs">
                  TC
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={16} className="ff-regular fs-xxxs">
                  Transaction Count
                </Col>
              </Row>
              <Row className="mb-4">
                <Col span={6} className="ff-regular fs-xxxs">
                  AC
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={16} className="ff-regular fs-xxxs">
                  Average Check
                </Col>
              </Row>
              <Row className="mb-4">
                <Col span={6} className="ff-regular fs-xxxs">
                  Max queue/hr
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={16} className="ff-regular fs-xxxs">
                  peak no. of customers in queue within an hour
                </Col>
              </Row>
              <Row>
                <Col span={6} className="ff-regular fs-xxxs">
                  Served on time
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={16} className="ff-regular fs-xxxs">
                  % of orders served within target
                </Col>
              </Row>
            </div>
          }
        />
      </div>

      <div style={styles.grid}>
        <div style={styles.row}>
          <div />
          {dates.map((item) => {
            const dayOfWeek = dateFormatter(item.toISOString(), 'ddd');

            return (
              <div
                key={dayOfWeek}
                className="flex flex-align-center flex-justify-center ff-regular fs-xxs color-gray-1"
              >
                {dayOfWeek.slice(0, 1)}
              </div>
            );
          })}
        </div>

        {renderRow(EMetricType.Sales)}
        {renderRow(EMetricType.TransactionCount)}
        {renderRow(EMetricType.AverageCheck)}

        <div style={styles.row}>
          <div className="mr-3 flex flex-align-center">
            <span className="ff-regular fs-xxs ellipsis">{EMetricTypeLabel[EMetricType.MaxQueuePerHour]}</span>
          </div>
          {dailyMetrics[EMetricType.MaxQueuePerHour].map((item, index) => {
            return (
              <div
                key={index}
                style={styles.col}
                className="flex flex-align-center flex-justify-center ff-regular fs-xxs color-gray-1"
              >
                {item}
              </div>
            );
          })}
        </div>

        <div style={styles.row}>
          <div className="mr-3 flex flex-align-center">
            <span className="ff-regular fs-xxs ellipsis">{EMetricTypeLabel[EMetricType.ServedOnTime]}</span>
          </div>
          {dailyMetrics[EMetricType.ServedOnTime].map((item, index) => {
            return (
              <div
                key={index}
                style={styles.col}
                className="flex flex-align-center flex-justify-center ff-regular fs-xxs color-gray-1"
              >
                {item}%
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
